import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { axiosApi } from "./axios";
import {
  CreateFileBoDto,
  CreateFileDto,
  CreateFileResponseDto,
  UpdateFileBoDto,
} from "@/types/api/api-types";

//#region Post

const createFileBo = async (payload: CreateFileBoDto) => {
  const response = await axiosApi.post<CreateFileResponseDto>("v1/file/bo", payload);
  return response.data;
};

export const useCreateFileBo = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: createFileBo,
    onError: () => {
      toast.error(t("fileApi.createError"));
    },
  });
};

const createFile = async (payload: CreateFileDto) => {
  const response = await axiosApi.post<CreateFileResponseDto>("v1/file", payload);
  return response.data;
};

export const useCreateFile = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: createFile,
    onError: () => {
      toast.error(t("fileApi.createError"));
    },
  });
};

//#endregion

//#region Patch

const updateFileBo = async ({ id, payload }: { id: string; payload: UpdateFileBoDto }) => {
  const response = await axiosApi.patch(`v1/file/bo/${id}`, payload);
  return response.data;
};

export const useUpdateFileBo = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: updateFileBo,
    onError: () => {
      toast.error(t("fileApi.updateError"));
    },
  });
};

type FedexUploadDocumentsErrorPayload = {
  output?: {
    documentResponses?: {
      errors?: {
        message?: string;
      };
    };
  };
  errors?: {
    message?: string;
  };
};

const getFedexUploadDocumentsErrorDescription = (data: unknown): string | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const payload = data as FedexUploadDocumentsErrorPayload;
  const documentMessage = payload.output?.documentResponses?.errors?.message;

  if (typeof documentMessage === "string" && documentMessage.length > 0) {
    return documentMessage;
  }

  const topLevelMessage = payload.errors?.message;

  if (typeof topLevelMessage === "string" && topLevelMessage.length > 0) {
    return topLevelMessage;
  }

  return null;
};

const getFedexUploadDocumentsErrorDescriptionFromAxiosError = (error: unknown): string | null => {
  if (!isAxiosError(error)) {
    return null;
  }

  const responseData = error.response?.data;

  if (typeof responseData !== "object" || responseData === null) {
    return null;
  }

  const payload = "message" in responseData ? responseData.message : responseData;

  return getFedexUploadDocumentsErrorDescription(payload);
};

const showSendFileViaApiErrorToast = (error: unknown, title: string) => {
  const description = getFedexUploadDocumentsErrorDescriptionFromAxiosError(error);

  if (!description) {
    toast.error(title);
    return;
  }

  toast.error(
    <div className="max-w-sm break-words">
      <p className="font-semibold">{title}</p>
      <p className="mt-1 text-sm leading-snug font-normal">{description}</p>
    </div>,
    { className: "!max-w-sm !w-full" },
  );
};

const sendFileViaApiBo = async ({ id, expeditionId }: { id: string; expeditionId: string }) => {
  const response = await axiosApi.patch(`v1/file/bo/${id}/send-via-api`, { expeditionId });
  return response.data;
};

export const useSendFileViaApiBo = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: sendFileViaApiBo,
    onError: (error) => {
      showSendFileViaApiErrorToast(error, t("fileApi.sendViaApiError"));
    },
  });
};

//#endregion

//#region Delete

const deleteFileByIdBo = async (id: string) => {
  return axiosApi.delete(`v1/file/bo/${id}`);
};

export const useDeleteFileByIdBo = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: deleteFileByIdBo,
    onError: () => {
      toast.error(t("fileApi.deleteError"));
    },
  });
};

const deleteFileById = async (id: string) => {
  return axiosApi.delete(`v1/file/${id}`);
};

export const useDeleteFileById = () => {
  const { t } = useTranslation("boExpeditionForm");
  return useMutation({
    mutationFn: deleteFileById,
    onError: () => {
      toast.error(t("fileApi.deleteError"));
    },
  });
};

//#endregion
